import { useCallback } from "react";
import { useAuthStore } from "../stores/authStore";
import { ensureFreshToken } from "../utils/tokenSession";
import { getApiBaseUrl } from "../utils/apiConfig";

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function useApi() {
  const token = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);

  const request = useCallback(
    async <T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> => {
      const { params, ...fetchOptions } = options;

      const base = getApiBaseUrl();
      let url = `${base}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
      if (params) {
        const searchParams = new URLSearchParams(params);
        url += `?${searchParams.toString()}`;
      }

      // Renueva silenciosamente si el token está por expirar antes de llamar.
      await ensureFreshToken();
      const requestToken = useAuthStore.getState().token ?? token;

      const buildHeaders = (activeToken: string | null): Record<string, string> => {
        const headers: Record<string, string> = {
          ...(fetchOptions.headers as Record<string, string>),
        };
        if (activeToken) headers["Authorization"] = `Bearer ${activeToken}`;
        if (
          fetchOptions.body &&
          typeof fetchOptions.body === "string" &&
          !headers["Content-Type"]
        ) {
          headers["Content-Type"] = "application/json";
        }
        return headers;
      };

      const rawFetch = (activeToken: string | null) =>
        fetch(url, { ...fetchOptions, headers: buildHeaders(activeToken) });

      const executeWithRetries = async (activeToken: string | null): Promise<Response> => {
        let retries = 2;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            return await rawFetch(activeToken);
          } catch (fetchErr: any) {
            if (
              retries > 0 &&
              (fetchErr.name === "TypeError" ||
                fetchErr.message?.includes("network") ||
                fetchErr.message?.includes("Failed to fetch"))
            ) {
              retries--;
              await new Promise((r) => setTimeout(r, 400));
              continue;
            }
            throw fetchErr;
          }
        }
      };

      let response = await executeWithRetries(requestToken);

      if (response.status === 401 && requestToken) {
        // Solo actúa si el token que falló es el activo (evita afectar la cuenta recién seleccionada).
        const currentToken = useAuthStore.getState().token;
        if (currentToken === requestToken) {
          const ok = await useAuthStore.getState().refreshAccessToken();
          if (ok) {
            const newToken = useAuthStore.getState().token;
            response = await rawFetch(newToken);
          }
        }
        if (response.status === 401) {
          const stillCurrent = useAuthStore.getState().token;
          if (requestToken && stillCurrent === requestToken) {
            logout();
          }
          throw new Error("Sesión expirada. Inicia sesión nuevamente.");
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.error || errorData.message || `Error ${response.status}`,
          response.status,
        );
      }

      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    },
    [token, logout],
  );

  const get = useCallback(
    <T = unknown>(endpoint: string, params?: Record<string, string>, options?: RequestInit) =>
      request<T>(endpoint, { method: "GET", params, ...options }),
    [request],
  );

  const post = useCallback(
    <T = unknown>(endpoint: string, body?: unknown) =>
      request<T>(endpoint, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),
    [request],
  );

  const put = useCallback(
    <T = unknown>(endpoint: string, body?: unknown) =>
      request<T>(endpoint, {
        method: "PUT",
        body: body ? JSON.stringify(body) : undefined,
      }),
    [request],
  );

  const patch = useCallback(
    <T = unknown>(endpoint: string, body?: unknown) =>
      request<T>(endpoint, {
        method: "PATCH",
        body: body ? JSON.stringify(body) : undefined,
      }),
    [request],
  );

  const del = useCallback(
    <T = unknown>(endpoint: string) =>
      request<T>(endpoint, { method: "DELETE" }),
    [request],
  );

  return { request, get, post, put, patch, del };
}